import { customColors } from "./colors";

/**
 * Semantic design tokens. Pages and components should reference these
 * (via `theme.palette.ae.*`) instead of raw `customColors` so the whole
 * look can be changed from this one file. With `cssVariables: true` in the
 * theme, each entry is also emitted as `--mui-palette-ae-<key>`, so a
 * runtime theme can override them in CSS without a rebuild.
 */
export const semanticTokens = {
  // surfaces
  // Cool page ground: the same value the rest of the app already calls
  // `$light-bg` (styles/_colors.scss, exposed as `--light`). It sits between
  // slate00 and slate10, which the ramp has no stop for, so it stays a literal
  // here - a step lighter than `surfaceMuted` so the slate sidebar reads as a
  // panel on top of it and white cards still lift off it.
  bg: "#FBFCFC",
  surface: customColors.slate.slate00,
  surfaceMuted: customColors.slate.slate10,
  surfaceHover: customColors.slate.slate20,
  // lines (design system: hairline = gray-200, input border = gray-300, search border = teal-400)
  border: customColors.slate.slate30,
  borderStrong: customColors.slate.slate40,
  searchBorder: customColors.viking.viking40,
  // .search-box-wrapper:focus-within in the design system = teal-600
  searchBorderFocus: customColors.viking.viking60,
  // text
  text: customColors.slate.slate100,
  textMuted: customColors.slate.slate60,
  textSubtle: customColors.slate.slate50,
  // brand accent
  accent: customColors.viking.viking70,
  accentHover: customColors.viking.viking80,
  accentSoft: customColors.viking.viking10,
  // Tinted card fill, a step lighter than accentSoft.
  accentSurface: customColors.viking.viking00,
  accentText: customColors.slate.slate00,
  accentBadge: customColors.viking.viking50,
  // dark fill for applied chips / secondary buttons (navy-900)
  inverse: customColors.slate.slate100,
  inverseText: customColors.slate.slate00,
  // status
  success: customColors.green.green60,
  // icon tiles on cards
  tileBg: customColors.viking.viking00,
  tileFg: customColors.viking.viking70
} as const;

export type SemanticTokens = { [K in keyof typeof semanticTokens]: string };

/** Non-colour tokens from the AptEdge design system export (aptedge-design-system.html). */
export const designTokens = {
  fontSans: `"DM Sans", system-ui, -apple-system, sans-serif`,
  fontMono: `"DM Mono", "Fira Code", monospace`,
  radius: { xs: 3, sm: 4, md: 8, lg: 12, xl: 16, "2xl": 20, full: 9999 },
  hairline: "0.5px solid",
  shadowSm: "0 1px 3px rgba(26,43,60,0.08), 0 1px 2px rgba(26,43,60,0.04)",
  shadowMd: "0 4px 12px rgba(26,43,60,0.08), 0 2px 4px rgba(26,43,60,0.04)",
  focusRing: "0 0 0 3px rgba(44,188,171,0.25)",
  transitionFast: "80ms ease",
  transitionBase: "140ms ease"
} as const;
